import React, { useState, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { Controller, useFormContext, ControllerRenderProps } from 'react-hook-form';
import { Input, Select, PendingUpload } from 'alurkerja-ui';
import SwitchButton from './SwitchButton';
import { MarkdownInput } from '@/components/ui/MarkdownInput';
import RichTextEditor from '@/components/ui/RichTextEditor';

export interface FormField {
  name: string;
  label: string;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'password'
    | 'select'
    | 'textarea'
    | 'switch'
    | 'upload'
    | 'richtext'
    | 'markdown';
  uploadType?: 'image' | 'file';
  options?: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  onFileChange?: (file: File | null) => void;
}

interface FormBuilderProps {
  fields: FormField[];
  initialPublished?: boolean;
  onPublishChange?: (value: boolean) => void;
  hidePublish?: boolean;
}

function getFieldRules(field: FormField) {
  if (field.type === 'richtext') {
    return {
      validate: (v: string) => {
        const cleaned = v.replace(/<(.|\n)*?>/g, '').trim();
        return cleaned.length > 0 || `${field.label} is required`;
      },
    };
  }
  if (field.required) return { required: `${field.label} is required` };
  return undefined;
}

function renderFieldInput(field: FormField, f: ControllerRenderProps<any, string>) {  switch (field.type) {
  case 'date':
    return (
      <Input
        {...f}
        type="date"
        className="text-sm w-full"
        placeholder={field.placeholder || 'Select date'}
      />
    );

  case 'select':
    return (
      <Select
        size="sm"
        placeholder={field.placeholder || 'Select option'}
        className="text-sm w-full"
        options={field.options || []}
        value={f.value ? { label: f.value, value: f.value } : null}
        onChange={(v: any) => f.onChange(v.value)}
      />
    );

  case 'textarea':
    return <Input textArea {...f} className="text-sm w-full" placeholder={field.placeholder} />;

  case 'switch':
    return (
      <div className="flex items-center justify-between border border-gray-200 rounded-md p-2">
        <span className="text-sm">{field.label}</span>
        <SwitchButton checked={f.value} onChange={f.onChange} />
      </div>
    );

  case 'upload': {
    const isImageUpload = field.uploadType === 'image';
    return (
      <div>
        <PendingUpload
          asFile
          multiple={false}
          value={f.value ? [f.value] : []}
          onChange={(files: any[]) => {
            const fileObj = files?.[0];
            let file: File | null = null;

            if (fileObj instanceof File) {
              file = fileObj;
            } else if (fileObj?.file instanceof File) {
              file = fileObj.file;
            }

            f.onChange(file);
            field.onFileChange?.(file);
          }}
          allowedExtension={
            field.uploadType === 'image' ? ['jpg', 'jpeg', 'png', 'webp'] : ['pdf', 'doc', 'docx']
          }
        />

        {!isImageUpload && (
          <small className="text-gray-500 mt-1">
              Supports: <strong>PDF, DOC, DOCX</strong> only.
          </small>
        )}
        {isImageUpload && (
          <small className="text-gray-500 mt-1">
              Supports: <strong>JPG, JPEG, PNG, or WEBP</strong> only.
          </small>
        )}
      </div>
    );
  }

  case 'richtext':
    return <RichTextEditor value={f.value || ''} onChange={f.onChange} />;

  case 'markdown':
    return (
      <MarkdownInput
        value={f.value || ''}
        onChange={f.onChange}
        className="w-full mt-1"
        placeholder={field.placeholder || 'Supports *bold*, _italic_, ~~strike~~, `code`'}
      />
    );

  default:
    return (
      <Input
        {...f}
        type={field.type || 'text'}
        className="text-sm w-full"
        placeholder={field.placeholder || field.label}
      />
    );
  }
}

export default function FormBuilder({
  fields,
  initialPublished = false,
  onPublishChange,
  hidePublish,
}: Readonly<FormBuilderProps>) {
  const { control } = useFormContext();
  const [isPublished, setIsPublished] = useState(initialPublished);

  useEffect(() => {
    setIsPublished(initialPublished);
  }, [initialPublished]);

  return (
    <div className="space-y-4 mx-5 md:mx-1">
      {fields.map(field => (
        <div key={field.name}>
          <label className="block mb-1 text-sm font-medium">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>

          <Controller
            name={field.name}
            control={control}
            rules={getFieldRules(field)}
            render={({ field: f, fieldState: { error } }) => (
              <div>
                {renderFieldInput(field, f)}
                {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
              </div>
            )}
          />
        </div>
      ))}

      {!hidePublish && (
        <div className="flex gap-1 items-center">
          <SwitchButton
            checked={isPublished}
            onChange={value => {
              setIsPublished(value);
              onPublishChange?.(value);
            }}
          />
          <span className="text-sm font-semibold">Publish</span>
        </div>
      )}
    </div>
  );
}
